"use client";

import React, { useEffect, useState } from "react";
import { Shield, Download, Share2, CircleAlert, CircleCheck, Clock, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
const activeLicenses = [{
  id: "lic-1",
  type: "One-Time Gift",
  issuer: "Immediate Impact",
  issuerDetails: "Your single contribution provides immediate resources for reentry support and essential life skills training.",
  daysLeft: 100,
  totalDays: 100,
  status: "good"
}, {
  id: "lic-2",
  type: "Monthly Sustainer",
  issuer: "Long-term Stability",
  issuerDetails: "Recurring donations allow us to plan for future initiatives like the Pathways to Success Center.",
  daysLeft: 30,
  totalDays: 30,
  status: "good"
}, {
  id: "lic-3",
  type: "Founders Circle",
  issuer: "Legacy Leadership",
  issuerDetails: "A special group of initial supporters helping build the foundation for transformational community support.",
  daysLeft: 365,
  totalDays: 365,
  status: "good"
}, {
  id: "lic-4",
  type: "Volunteer Service",
  issuer: "Community Mentorship",
  issuerDetails: "Give your time and skills to mentor individuals navigating their journey toward independent living.",
  daysLeft: 12,
  totalDays: 12,
  status: "warning"
}, {
  id: "lic-5",
  type: "Corporate Partner",
  issuer: "Strategic Growth",
  issuerDetails: "Partner with us to provide transportation services and community resource navigation for those in need.",
  daysLeft: 90,
  totalDays: 90,
  status: "good"
}];
export default function LicenseTrackingServices() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const getStatusColor = (status: string) => {
    switch (status) {
      case "critical":
        return "var(--destructive)";
      case "warning":
        return "var(--chart-3)";
      case "good":
      default:
        return "var(--chart-2)";
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "critical":
        return <CircleAlert className="size-5 text-destructive" />;
      case "warning":
        return <Clock className="size-5 text-chart-3" />;
      case "good":
      default:
        return <CircleCheck className="size-5 text-chart-2" />;
    }
  };
  return <section data-section-id="222163" id="ways-to-give" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary-foreground text-sm font-medium mb-2">
              <Shield className="size-4" />
              Support Our Mission
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-default text-primary tracking-tight">
              Ways to Give & Support
            </h2>
            <p className="text-muted-foreground text-lg font-default">
              Your generosity restores dignity and opportunity. Choose the path that best reflects your commitment to second chances.
            </p>
          </div>
          <Button asChild variant="outline" className="shrink-0 border-primary text-primary hover:bg-primary/5 rounded-full">
            <Link to="/SupportUsPage">
              All Giving Options <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activeLicenses.map((license, i) => {
          const percentage = Math.max(0, Math.min(100, license.daysLeft / license.totalDays * 100));
          const statusColor = getStatusColor(license.status);
          return <Card key={license.id} data-index={i} className="group relative overflow-hidden border-border shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col rounded-2xl bg-card">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start gap-4">
                    <div className="space-y-1.5">
                      <CardTitle className="text-xl font-bold font-default leading-tight text-primary">
                        {license.type}
                      </CardTitle>
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <div className="text-sm text-muted-foreground hover:text-secondary-foreground hover:underline cursor-help inline-block font-default">
                            {license.issuer}
                          </div>
                        </HoverCardTrigger>
                        <HoverCardContent align="start" className="w-80 z-50 rounded-xl border-border bg-popover shadow-lg">
                          <div className="space-y-2">
                            <h4 className="text-sm font-semibold font-default text-foreground">
                              {license.issuer}
                            </h4>
                            <p className="text-sm text-muted-foreground font-default">
                              {license.issuerDetails}
                            </p>
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    </div>
                    <div className="shrink-0 bg-background rounded-full p-1 shadow-sm border border-border">
                      {getStatusIcon(license.status)}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col items-center justify-center py-8">
                  <div className="relative size-36 flex items-center justify-center">
                    <svg className="size-full -rotate-90 transform" viewBox="0 0 36 36">
                      <path className="text-muted stroke-current" strokeWidth="2" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                      <path className="stroke-current transition-all duration-1000 ease-out" style={{
                    color: statusColor,
                    strokeDasharray: `${mounted ? percentage : 0}, 100`
                  }} strokeWidth="2.5" strokeLinecap="round" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                    </svg>
                    
                    <div className="absolute flex flex-col items-center justify-center text-center">
                      <span className="text-4xl font-bold font-default tracking-tighter" style={{
                    color: statusColor
                  }}>
                        {license.daysLeft}%
                      </span>
                      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mt-1 font-default">
                        Goal Progress
                      </span>
                    </div>
                  </div>
                </CardContent>

                <div className="absolute inset-0 bg-primary/95 backdrop-blur-md opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-center gap-3 p-6 translate-y-4 group-hover:translate-y-0">
                  <div className="text-center mb-2">
                    <p className="font-bold font-default text-primary-foreground text-lg">{license.type}</p>
                    <p className="text-sm text-primary-foreground/80 font-default">Join the Movement</p>
                  </div>
                  <Button variant="default" className="w-full shadow-md bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full font-default">
                    <Download className="mr-2 size-4" /> Get Info Packet
                  </Button>
                  <Button variant="outline" className="w-full bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 rounded-full font-default">
                    <Share2 className="mr-2 size-4" /> Share Mission
                  </Button>
                </div>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}